// CatalogAdminTab — ✦-Reiter des Katalogs: admin-only Übersicht der von außen eingelieferten
// Kartenvorschläge (POST /api/deck-suggestions, Polling über useCardSuggestions). Übernehmen
// läuft über den vom Rahmen übergebenen Add-Fluss (Toast/Duplikat-Behandlung liegt dort, siehe
// `CardCatalog.handleAdd`) — erst ein erfolgreicher Add löscht den Vorschlag serverseitig,
// sonst holt der nächste Poll die Karte zurück.
import React, { useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import CardGrid from '../../../ui/CardGrid';
import EmptyState from '../../../ui/EmptyState';
import { useToast } from '../../../ui/Toast';
import { SuggestionCardTile } from '../components/SuggestionTile';
import { useCardSuggestions } from '../hooks/useCardSuggestions';

/**
 * @param {string} deckId — UI-ID des Decks („virtual_25", per `deckUiId` aus der Zahl). Der
 *   Absender legt den Vorschlag unter der UI-ID ab, und `selectSuggestionIds` vergleicht sie
 *   strikt — mit der numerischen ID bliebe der Reiter dauerhaft leer.
 * @param {boolean} canAdd — Plus-Knöpfe zeigen (eigenes, bearbeitbares Deck)
 * @param {(entry) => Promise<{card, duplicate}|null>} onAdd — Add-Fluss des Katalogs
 *   (`CardCatalog.handleAdd`); `entry` braucht nur `id` und `name`
 * @param {(card) => void} onOpenCard — Klick auf die Kachel (Karten-Detail)
 */
export default function CatalogAdminTab({ deckId, canAdd = false, onAdd, onOpenCard }) {
  const { push: pushToast } = useToast();
  const { cards, dismissAll, dismissCard } = useCardSuggestions(deckId);

  // DELETE kann fehlschlagen (json() wirft bei jeder Nicht-2xx-Antwort) — ohne
  // .catch() liefe das als unbehandelte Promise-Ablehnung durch und der Nutzer
  // erführe nichts davon, obwohl die Karte beim nächsten Poll wieder auftaucht.
  const handleDismissCard = useCallback((scryfallId) => {
    dismissCard(scryfallId).catch(() => {
      pushToast({ tone: 'error', message: 'Vorschlag konnte nicht verworfen werden.' });
    });
  }, [dismissCard, pushToast]);

  const handleDismissAll = useCallback(() => {
    dismissAll().catch(() => {
      pushToast({ tone: 'error', message: 'Vorschläge konnten nicht verworfen werden.' });
    });
  }, [dismissAll, pushToast]);

  const handleAdd = useCallback(async (scryfallId) => {
    const entry = cards.find((c) => c.id === scryfallId);
    if (!entry) return;
    const result = await onAdd?.(entry);
    // Nur nach erfolgreichem Add verwerfen: bei Duplikat bleibt der Vorschlag stehen.
    if (result?.card) handleDismissCard(scryfallId);
  }, [cards, onAdd, handleDismissCard]);

  if (cards.length === 0) {
    return (
      <div className="card-catalog__tab">
        <EmptyState icon={Sparkles} title="Keine geschickten Karten" size="compact" />
      </div>
    );
  }

  return (
    <div className="card-catalog__tab">
      <div className="card-catalog__admin-header">
        <button type="button" className="card-catalog__link" onClick={handleDismissAll}>
          Alle verwerfen
        </button>
      </div>
      <CardGrid variant="browse">
        {cards.map((card) => (
          <SuggestionCardTile
            key={card.id}
            card={card}
            canAdd={canAdd}
            onAdd={handleAdd}
            onDismissCard={handleDismissCard}
            onOpen={onOpenCard}
            asTile
          />
        ))}
      </CardGrid>
    </div>
  );
}
